import type { IDisplayColorVariationAttribute } from '@/react/view-models/ProductVM/IDisplayVariationAttribute';

/**
 * Inputs for {@link getArtlabsStyleColorKeys} — Artlabs SDK style-color keys derived from PDP selection.
 */
export interface IArtlabsStyleColorKeysParams {
  /**
   * The style number.
   */
  styleNumber: string;
  /**
   * The current color code from the PDP selection (required field; pass `''` when unset).
   */
  selectedColor: string;
  /**
   * The color variation attributes from {@link IProductVM.getVariationAttributesByType}(`Color`).
   */
  availableColorAttributes: ReadonlyArray<IDisplayColorVariationAttribute>;
}

/**
 * Derives Artlabs SDK style-color keys from style number and color selection.
 * Used for `checkAvailability`, `startAR` (single key), and `startVTO` (comma-separated keys).
 * When `selectedColor` is empty, returns empty strings so callers never send invalid keys (e.g. `…-undefined`).
 * @param params - The parameters for {@link getArtlabsStyleColorKeys}.
 * @returns The style color and all style colors.
 */
export function getArtlabsStyleColorKeys({
  styleNumber,
  selectedColor,
  availableColorAttributes
}: IArtlabsStyleColorKeysParams): {
  styleColor: string;
  allStyleColors: string;
} {
  if (!selectedColor) {
    return { styleColor: '', allStyleColors: '' };
  }

  const styleColor = `${styleNumber}-${selectedColor}`;

  let allStyleColors = '';
  if (availableColorAttributes.length === 0) {
    // When no other color attributes exist, use only the current styleColor.
    allStyleColors = styleColor;
  } else {
    // The virtual try-on widget has functionality to change the color selection without exiting the widget.
    // To enable this, we need to pass all available colors for the SDK method. The currently selected color
    // is always first in the list so the widget opens to that color.
    const parts: Array<string> = [styleColor];
    for (const c of availableColorAttributes) {
      if (c.value !== selectedColor) {
        parts.push(`${styleNumber}-${c.value}`);
      }
    }
    allStyleColors = parts.join(',');
  }

  return { styleColor, allStyleColors };
}
